import React from 'react';
import { Link } from 'react-router-dom';
import { Product } from 'src/model/Product';
import { toCurrency } from 'src/helpers/currency/toCurrency';

interface ProductCard3Props {
  products: Product[];
  valuePrice: (product: Product, discount: boolean) => string;
  userCity?: string;
}

export const ProductCard3: React.FC<ProductCard3Props> = ({
  products,
  valuePrice,
  userCity,
}) => {
  const getProductPrice = (product: Product) => {
    const priceWithDiscount = valuePrice(product, true);
    const priceWithoutDiscount = valuePrice(product, false);

    const isValidPrice = (price: string) => {
      return price && price !== 'R$ 0,00' && price !== 'R$ NaN';
    };

    if (product.promotion !== false && isValidPrice(priceWithDiscount)) {
      return priceWithDiscount;
    }

    if (isValidPrice(priceWithoutDiscount)) {
      return priceWithoutDiscount;
    }

    if (product.grade?.[0]?.gradeSizes?.[0]?.price?.price) {
      return toCurrency(product.grade[0].gradeSizes[0].price.price);
    }

    return 'Consultar';
  };

  const getOriginalPrice = (product: Product) => {
    const firstGrade = product.grade?.[0];
    const firstPrice = firstGrade?.gradeSizes?.[0]?.price;

    if (firstPrice?.price && firstPrice?.discount && firstPrice.discount > 0) {
      return toCurrency(firstPrice.price);
    }

    return null;
  };

  const hasValidDiscount = (product: Product) => {
    const firstGrade = product.grade?.[0];
    const firstPrice = firstGrade?.gradeSizes?.[0]?.price;
    return firstPrice?.discount && firstPrice.discount > 0;
  };

  const getDiscountPercentage = (product: Product) => {
    const firstGrade = product.grade?.[0];
    const firstPrice = firstGrade?.gradeSizes?.[0]?.price;
    return firstPrice?.discount || 0;
  };

  const isLocalShipping = (product: Product) => {
    return (
      userCity &&
      product.storeSeller?.city &&
      userCity.toLowerCase() === product.storeSeller.city.toLowerCase()
    );
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1 md:gap-4 w-full max-w-screen-xl mx-auto px-3">
      {products.map((product) => {
        const originalPrice = getOriginalPrice(product);
        const hasDiscount = hasValidDiscount(product);
        const discountPercent = getDiscountPercentage(product);
        const localShipping = isLocalShipping(product);

        return (
          <Link
            to={`/product/${product.id}`}
            key={product.id}
            className="w-full bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
          >
            <div className="relative overflow-hidden group">
              <div className="w-full aspect-[1/1] bg-white flex items-center justify-center overflow-hidden">
                {product.imagens && product.imagens.length > 0 ? (
                  <img
                    src={product.imagens[0]}
                    alt={product.name}
                    className="w-full h-full object-cover object-top transform transition-transform duration-300 group-hover:scale-105"
                  />
                ) : (
                  <div className="text-gray-400 text-sm">
                    Imagem indisponível
                  </div>
                )}
              </div>

              {product.tags && product.tags.length > 0 && (
                <div className="absolute top-2 left-2">
                  <div className="bg-red-500 text-white text-xs px-2 py-1 rounded">
                    {product.tags[0]}
                  </div>
                </div>
              )}
            </div>

            <div className="p-3">
              <h2
                className="text-sm font-semibold text-gray-800 leading-tight overflow-hidden mb-2 line-clamp-2"
                style={{ minHeight: '2.5rem' }}
                title={product.name}
              >
                {product.name}
              </h2>

              <div className="flex flex-col gap-1 mb-2">
                <div className="flex items-center gap-2">
                  {originalPrice && (
                    <span className="text-gray-400 text-xs line-through">
                      {originalPrice}
                    </span>
                  )}
                  {Boolean(hasDiscount && discountPercent > 0) && (
                    <span className="bg-green-100 text-green-600 text-xs font-semibold px-2 py-0.5 rounded">
                      {discountPercent}%OFF
                    </span>
                  )}
                </div>
                <span className="text-red-500 font-bold text-lg">
                  {getProductPrice(product)}
                </span>
              </div>

              {(product.freeShipping || localShipping) && (
                <div className="mb-2">
                  <span className="text-green-600 text-xs font-semibold">
                    {localShipping ? 'Frete local grátis' : 'Frete grátis'}
                  </span>
                  {localShipping && (
                    <div className="text-blue-800 text-xs">
                      Entrega em até 2 horas após a compra
                    </div>
                  )}
                </div>
              )}

              {Boolean(product.totalSale && product.totalSale > 0) && (
                <div className="text-gray-600 text-xs">
                  {product.totalSale} vendidos
                </div>
              )}
            </div>
          </Link>
        );
      })}
    </div>
  );
};
